import React, { useCallback, useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import styled from 'styled-components';
import { Button, IconButton, TextField } from '@material-ui/core';
import AddCircleOutlineOutlinedIcon from '@material-ui/icons/AddCircleOutlineOutlined';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import HighlightOffOutlinedIcon from '@material-ui/icons/HighlightOffOutlined';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { OptionListType } from '@sierralabs/forms-core';
import { useFormContext } from '../FormContext';
import ConfigFieldControl from './components/ConfigFieldControl';
const StyledList = styled.ul `
  margin: 0;
  padding: 0;
  list-style: none;
  li {
    display: flex;
    margin: 0 -12px 0 -12px;
    padding: 6px 6px 6px 0;
    &.dragging {
      opacity: 0.7;
      background: #fff;
      /* border: solid 1px #ccc; */
      border-radius: 4px;
      box-shadow: 0px 2px 1px -1px rgba(0, 0, 0, 0.2),
        0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12);
      .drag-handle {
        opacity: 1;
      }
    }
    &.add-option {
      margin-left: 0;
    }
    &:hover .drag-handle {
      opacity: 1;
    }
    .drag-handle {
      display: flex;
      align-items: center;
      opacity: 0.25;
      transition: opacity 300ms;
      svg {
        color: #bbb;
      }
    }
    .field-value {
      margin-right: 4px;
    }
    .field-label {
      margin-left: 4px;
    }
    .button-container {
      display: flex;
      align-items: center;
    }
  }
`;
const getFreeFormText = (options) => {
    if (options.some(o => o.value !== o.label)) {
        return options.map(o => `${o.value}|${o.label}`).join('\n');
    }
    else {
        return options.map((option) => option.value).join('\n');
    }
};
export const FormConfigOptions = ({ multiselect }) => {
    var _a;
    const { formState, setFormState, notifyFormEdit, elementPlugins, entitySchema } = useFormContext();
    const element = formState.selectedFormElement;
    const column = entitySchema === null || entitySchema === void 0 ? void 0 : entitySchema.columns.find(column => column.name === element.dataSource);
    const isEnumType = (column === null || column === void 0 ? void 0 : column.type) === 'enum';
    const plugin = elementPlugins.find(p => p.type === element.type);
    if (!element.config)
        element.config = { type: OptionListType.List, options: [] };
    if (!element.config.options)
        element.config.options = [];
    element.config.options = element.config.options.map(o => ['string', 'number', 'boolean'].includes(typeof o)
        ? { value: o, label: o.toString() }
        : o);
    const [freeForm, setFreeForm] = useState('');
    const [options, setOptions] = useState([]);
    useEffect(() => {
        let options = element.config.options;
        // if plugin is boolean make sure the options return boolean values
        if ((plugin === null || plugin === void 0 ? void 0 : plugin.type) === 'Boolean') {
            if (options.length > 2) {
                options.length = 2;
            }
            else if (options.length < 2) {
                options = [
                    { value: true, label: 'Yes' },
                    { value: false, label: 'No' }
                ];
            }
            // make sure both options have boolean values
            if (typeof options[0].value !== 'boolean' ||
                typeof options[1].value !== 'boolean') {
                options[0].value = true;
                options[1].value = false;
            }
            element.config.options = [...options];
        }
        else {
            // populate free form text field
            setFreeForm(getFreeFormText(options));
        }
        setOptions(options);
    }, [element.config, plugin === null || plugin === void 0 ? void 0 : plugin.type]);
    const [listType, setListType] = useState(element.config.type || OptionListType.List);
    // check if column option values match entity enum, and if not then update
    // the form element config options.
    useEffect(() => {
        var _a;
        // TOOD: consider allowing admin to remove options because some situations
        // it makes sense to have not all options enabled; however, if removing, the
        // admins should be able to add back options.
        if (column && isEnumType) {
            const enumOptions = Object.values(column.enum || {});
            const configOptions = ((_a = element.config) === null || _a === void 0 ? void 0 : _a.options) || [];
            if (configOptions.length > 0 &&
                enumOptions.length === configOptions.length &&
                configOptions.every(o => enumOptions.includes(typeof o === 'object' ? o.value : o))) {
                // if all element's enums match then don't update element config
                return;
            }
            const options = enumOptions.map(option => ({
                value: option,
                label: option
            }));
            element.config = Object.assign(Object.assign({}, element.config), { options });
            setFormState(formState => (Object.assign(Object.assign({}, formState), { selectedFormElement: element })));
            notifyFormEdit();
        }
    }, [isEnumType, column, element, setFormState, notifyFormEdit]);
    const handleOptionUpdate = (option, index) => {
        // TODO: check that option values are unique
        setOptions([...options]);
        if (listType === OptionListType.List) {
            element.config.options[index] = option.value;
        }
        else {
            element.config.options[index] = option;
        }
        element.config = Object.assign({}, element.config); // force update
        setFormState(Object.assign(Object.assign({}, formState), { selectedFormElement: element }));
        notifyFormEdit();
    };
    const handleOptionsUpdate = useCallback((newOptions) => {
        const { options = [] } = element.config;
        if (newOptions) {
            options.splice(0, options.length, ...newOptions);
        }
        element.config = Object.assign(Object.assign({}, element.config), { options });
        setFormState(Object.assign(Object.assign({}, formState), { selectedFormElement: element }));
        notifyFormEdit();
    }, [element, formState, notifyFormEdit, setFormState]);
    return (React.createElement(React.Fragment, null,
        !((_a = plugin === null || plugin === void 0 ? void 0 : plugin.dataTypes) === null || _a === void 0 ? void 0 : _a.includes('boolean')) && (React.createElement(ConfigFieldControl, null,
            React.createElement(ToggleButtonGroup, { exclusive: true, size: 'small', value: listType, onChange: (event, newListType) => {
                    if (listType === newListType) {
                        // no change
                        return;
                    }
                    switch (newListType) {
                        case OptionListType.List:
                            element.config.options = options.map(o => o.value);
                            break;
                        case OptionListType.KeyPair:
                            element.config.options = [...options];
                            break;
                        case OptionListType.FreeForm:
                            setFreeForm(getFreeFormText(options));
                            break;
                    }
                    element.config.type = newListType;
                    setFormState(Object.assign(Object.assign({}, formState), { selectedFormElement: element }));
                    setListType(newListType);
                    notifyFormEdit();
                } },
                React.createElement(ToggleButton, { value: OptionListType.List }, "List"),
                React.createElement(ToggleButton, { value: OptionListType.KeyPair }, "Key/Pair"),
                React.createElement(ToggleButton, { value: OptionListType.FreeForm, disabled: isEnumType }, "Free Form")))),
        React.createElement(ConfigFieldControl, null, listType === OptionListType.FreeForm ? (React.createElement(TextField, { multiline: true, fullWidth: true, variant: 'outlined', className: 'field-value', value: freeForm, onChange: event => {
                const text = event.target.value;
                // parse key value pair
                const parsedOptions = text
                    .split('\n')
                    .map((line) => {
                    // split on first occurrence only
                    const splitIndex = line.indexOf('|');
                    // use same value if line has no split
                    const hasSplit = splitIndex > -1;
                    const key = hasSplit
                        ? line.slice(0, splitIndex)
                        : line;
                    const value = hasSplit
                        ? line.slice(splitIndex + 1, line.length)
                        : line;
                    return { value: key, label: value };
                });
                setOptions(parsedOptions);
                setFreeForm(text);
                handleOptionsUpdate(parsedOptions);
            } })) : (React.createElement(DragDropContext, { onDragEnd: result => {
                const { source, destination } = result;
                // dropped outside the list
                if (!destination) {
                    return;
                }
                const [removed] = options.splice(source.index, 1);
                options.splice(destination.index, 0, removed);
                element.config = Object.assign(Object.assign({}, element.config), { options: options.map(o => listType === OptionListType.List ? o.value : o) }); // force update
                setFormState(Object.assign(Object.assign({}, formState), { selectedFormElement: element }));
                notifyFormEdit();
            } },
            React.createElement(Droppable, { droppableId: 'options' }, (provided, snapshot) => (React.createElement(StyledList, { className: 'options-map', ref: provided.innerRef },
                options.map((option, index) => (React.createElement(Draggable, { key: index, index: index, draggableId: `draggable-${index}` }, (provided, snapshot) => (React.createElement("li", Object.assign({ ref: provided.innerRef }, provided.draggableProps, { className: snapshot.isDragging ? 'dragging' : '' }),
                    React.createElement("div", Object.assign({ className: 'drag-handle' }, provided.dragHandleProps, { tabIndex: -1 }),
                        React.createElement(DragIndicatorIcon, null)),
                    React.createElement(TextField, { fullWidth: true, size: 'small', variant: 'outlined', className: 'field-value', value: option.value, disabled: (plugin === null || plugin === void 0 ? void 0 : plugin.type) === 'Boolean' || isEnumType, onChange: event => {
                            option.value = event.target.value;
                            handleOptionUpdate(option, index);
                        } }),
                    (listType === OptionListType.KeyPair ||
                        (plugin === null || plugin === void 0 ? void 0 : plugin.type) === 'Boolean') && (React.createElement(TextField, { fullWidth: true, size: 'small', variant: 'outlined', value: option.label, className: 'field-label', onChange: event => {
                            option.label = event.target.value;
                            handleOptionUpdate(option, index);
                        } })),
                    React.createElement("div", { className: 'button-container' },
                        React.createElement(IconButton, { size: 'small', tabIndex: '-1', disabled: isEnumType, onClick: () => {
                                options.splice(index, 1);
                                setOptions([...options]);
                                element.config.options = options;
                                setFormState(Object.assign(Object.assign({}, formState), { selectedFormElement: element }));
                                notifyFormEdit();
                            } },
                            React.createElement(HighlightOffOutlinedIcon, null)))))))),
                provided.placeholder,
                React.createElement("li", { className: 'add-option' },
                    React.createElement(Button, { startIcon: React.createElement(AddCircleOutlineOutlinedIcon, null), disabled: isEnumType, onClick: () => {
                            const option = { value: '', label: '' };
                            setOptions([...options, option]);
                            handleOptionUpdate(option, options.length);
                        } }, "Add Option"))))))))));
};
export default FormConfigOptions;
//# sourceMappingURL=FormConfigOptions.js.map